import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import CourseCard from '../components/CourseCard';
import courseService from '../services/courseService';

const HomePage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        setLoading(true);
        setError('');
        const allCourses = await courseService.getAllCourses();
        setCourses(allCourses.slice(0, 3));
      } catch (err) {
        setError('Could not load featured courses. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const stats = [
    { number: "xy", label: "Subjects Covered" },
    { number: "xyz+", label: "Students Enrolled" },
    { number: "x+", label: "Expert Educators" },
    { number: "xy%", label: "Success Rate" }
  ];

  const features = [
    {
      icon: "📚",
      title: "Comprehensive Notes",
      description: "In-depth study materials with tips and tricks to master every topic systematically."
    },
    {
      icon: "📝",
      title: "Mock Tests & Series",
      description: "Practice with full-length mock tests to build confidence and improve time management."
    },
    {
      icon: "💡",
      title: "Previous Year Questions",
      description: "Analyze patterns and difficulty levels through extensive PYQ practice sessions."
    },
    {
      icon: "🎓",
      title: "Expert Guidance",
      description: "Learn from experienced educators through structured lectures and curated resources."
    }
  ];

  const renderFeaturedCourses = () => {
    if (loading) {
      return (
        <div className="text-center py-12">
          <div className="w-10 h-10 border-3 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-500 text-sm">Loading courses...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <p className="text-slate-500">{error}</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses.map((course) => (
          <CourseCard key={course.id} course={course} />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Minimal Clean Design */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-50"></div>

        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-40">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2364748b' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-32 lg:pb-32">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-slate-100 border border-slate-200">
              <span className="text-xs font-medium text-slate-700">Your path to UGC NET success</span>
            </div>
          </div>

          {/* Main Heading */}
          <h1 className="text-center text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-slate-900 mb-6 tracking-tight">
            Ace Your UGC NET
            <span className="block mt-2 bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent">
              LAW Exam
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-center text-lg sm:text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Prepare effectively for Assistant Professorship and JRF with exclusive study materials, mock tests, and expert guidance.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-16">
            <Link
              to="/courses"
              className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              Start Learning
              <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
            <Link
              to="/courses"
              className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-all duration-200"
            >
              Browse Courses
            </Link>
          </div>

          {/* Stats */}
          {/*<div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="text-center px-4 py-3 rounded-lg bg-white border border-slate-100">
                <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">{stat.number}</div>
                <div className="text-xs sm:text-sm text-slate-500 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>*/}
        </div>
      </section>

      {/* Features Section - Modern Card Design */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-3">
              Why Choose Lawfullingo?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A strategic approach with quality resources to boost your exam confidence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group relative bg-white rounded-xl p-6 border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex flex-col h-full">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center text-2xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses Section */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-3">
                Featured Courses
              </h2>
              <p className="text-lg text-slate-600 max-w-xl">
                Specialized courses for comprehensive UGC NET LAW preparation
              </p>
            </div>
            <Link
              to="/courses"
              className="mt-4 sm:mt-0 inline-flex items-center text-sm font-medium text-slate-700 hover:text-slate-900"
            >
              View all courses
              <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          {renderFeaturedCourses()}
        </div>
      </section>

      {/* CTA Section - Clean Minimal */}
      <section className="py-20 lg:py-24 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join thousands of aspirants improving their chances of success with focused study resources and expert guidance.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium text-slate-900 bg-white rounded-lg hover:bg-slate-100 transition-all duration-200 shadow-sm"
            >
              Get Started Free
            </Link>
            <Link
              to="/courses"
              className="inline-flex items-center justify-center px-7 py-3.5 text-sm font-medium text-white border border-slate-700 rounded-lg hover:bg-slate-800 transition-all duration-200"
            >
              Browse Courses
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
